import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getPurchaseStatus } from '../api';
import { LmsButton } from './LmsAccess';

const money = (cents) => (cents == null ? null : `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);

// Stripe hands the buyer back with ?training_checkout=success|canceled.
// Pure read - StrictMode runs state initializers twice, so the scrub below
// lives in an effect instead.
function readOutcome() {
  const params = new URLSearchParams(window.location.search);
  const outcome = params.get('training_checkout');
  // Only the two values the server emits. Anything else - a hand-edited or
  // truncated URL - used to fall through to the "Confirming your payment..."
  // branch with polling disabled, so the banner sat there forever. Same for a
  // success with no purchase id: there is nothing to poll, so there is nothing
  // to confirm.
  if (outcome !== 'success' && outcome !== 'canceled') return null;
  const purchaseId = params.get('training_purchase_id');
  if (outcome === 'success' && !purchaseId) return null;
  return { outcome, purchaseId };
}

// Drop the tags from the address bar so a refresh does not replay the banner
// and a copied link does not carry someone else's purchase id.
function scrubUrl() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('training_checkout') && !params.has('training_purchase_id')) return;
  params.delete('training_checkout');
  params.delete('training_purchase_id');
  const query = params.toString();
  window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
}

export default function CheckoutBanner({ onPaid }) {
  const qc = useQueryClient();
  const [ctx] = useState(readOutcome);
  const [dismissed, setDismissed] = useState(false);
  // Payment confirms over a webhook, so the buyer can land here before the
  // purchase flips. Poll for a bounded window, then tell them it is in flight
  // rather than spinning forever.
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(scrubUrl, []);

  const watching = !!ctx && ctx.outcome === 'success' && !!ctx.purchaseId && !dismissed;
  const { data } = useQuery({
    queryKey: ['training-purchase-status', ctx?.purchaseId],
    queryFn: () => getPurchaseStatus(ctx.purchaseId),
    // Stays enabled past the give-up point. Disabling it would kill polling AND
    // focus refetches, stranding the banner on "still processing" forever while
    // promising the page would update - a webhook that lands at 61s must still
    // flip it. After the fast window we just back off to a slow poll.
    enabled: watching,
    refetchInterval: (q) => {
      if (q.state.data?.status !== 'pending') return false;
      return gaveUp ? 15000 : 2500;
    },
  });

  const paid = data?.status === 'paid';

  useEffect(() => {
    if (!watching || gaveUp) return undefined;
    const timer = setTimeout(() => setGaveUp(true), 60000);
    return () => clearTimeout(timer);
  }, [watching, gaveUp]);

  useEffect(() => {
    if (!paid) return;
    qc.invalidateQueries({ queryKey: ['my-training-purchases'] });
    qc.invalidateQueries({ queryKey: ['my-training-requests'] });
    qc.invalidateQueries({ queryKey: ['my-training-seats'] });
    qc.invalidateQueries({ queryKey: ['my-training-orders'] });
    onPaid?.();
  }, [paid, qc, onPaid]);

  if (!ctx || dismissed) return null;

  if (ctx.outcome === 'canceled') {
    return (
      <Shell tone="neutral" onDismiss={() => setDismissed(true)}>
        <div className="text-sm font-semibold">Checkout canceled</div>
        <p className="text-xs mt-0.5">You were not charged. The class is still here whenever you are ready.</p>
      </Shell>
    );
  }

  if (paid) {
    return (
      <Shell tone="success" onDismiss={() => setDismissed(true)}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold">
              Payment received{data?.class_title ? ` for ${data.class_title}` : ''}
            </div>
            <p className="text-xs mt-0.5">
              {data?.seats ? `${data.seats} seat${data.seats === 1 ? '' : 's'}` : 'Your seats'}
              {data?.total_cents != null ? ` - ${money(data.total_cents)}` : ''}
              . Assign seats below, then open the LMS to start the class.
            </p>
          </div>
          <LmsButton url={data?.lms_url} />
        </div>
      </Shell>
    );
  }

  return (
    <Shell tone="info" onDismiss={() => setDismissed(true)}>
      <div className="text-sm font-semibold">
        {gaveUp ? 'Payment is still processing' : 'Confirming your payment...'}
      </div>
      <p className="text-xs mt-0.5">
        {gaveUp
          ? 'Stripe has your payment and we are still waiting on confirmation. This page keeps checking, and we email you either way.'
          : 'This takes a few seconds. Your class appears below the moment it clears.'}
      </p>
    </Shell>
  );
}

const TONES = {
  success: 'border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200',
  info: 'border-blue-200 bg-blue-50/60 text-blue-900 dark:border-gray-700 dark:bg-gray-800/60 dark:text-blue-200',
  neutral: 'border-orange-200 bg-orange-50 text-orange-900 dark:border-orange-900 dark:bg-orange-950/30 dark:text-orange-200',
};

function Shell({ tone, children, onDismiss }) {
  return (
    <div className={`rounded-xl border p-4 mb-4 ${TONES[tone]}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">{children}</div>
        <button onClick={onDismiss} aria-label="Dismiss" className="text-xs opacity-60 hover:opacity-100">
          Dismiss
        </button>
      </div>
    </div>
  );
}
